import { AppearObserver } from '@mtfe/appear-observer';
import { getABFromCookie } from './abUtil';
import { objectValues, objectEntries } from './utils';

// 数据上报实例
let collectInstance = null;

export class TCollector {
  /**
   * @fn: 初始化性能收集实例
   * @param String abType, 自定义ab实验参数（一般取项目名称，比如fe_mall）
   * @param Object times, 存储项目中首屏动态模块加载时间
   * @param Object times[url], 存储项目中页面地址为${url}页面的首屏动态模块加载时间
   * @param Number times[url][moduleName], 存储页面某模块加载时间
   * @param Object extra, 上报额外的业务自定义参数
   * @param Array stopElems, 监听到指定元素点击之后停止页面数据收集
   * @param Boolean stopCollectFlag, 是否停止数据收集（比如切换tab场景，第二个tab下的模块不属于首页首屏页面）
   * @param Object abInfo, cookie中获取的ab实验信息（`${layerId}` - ab实验策略；`${layerId}_s` - ab实验策略对应的开关）
   */
  constructor(options = {}) {
    // 单例模式
    if (collectInstance) return collectInstance;
    collectInstance = this;
    this.abType = options.abType || 'default';
    this.cookieConfig = options.cookieConfig;
    this.times = {};
    this.extra = {};
    this.stopElems = [];
    this.stopCollectFlag = false;
    this.abInfo = {};
    // report函数绑定当前实例
    this.reportHandler = this.report.bind(this);
    // handleStopCollect函数绑定当前实例
    this.stopCollectHandler = this.handleStopCollect.bind(this);
    // handleVisibilityChange函数绑定当前实例
    this.visibilityChangeHandler = this.handleVisibilityChange.bind(this);
    // 添加页面离开事件监听
    this.autoReport();
    // 添加会引起页面模块重新加载的事件监听
    this.stopListener();
    // 获取cookie中的ab信息
    this.getAbResult();
    // owl设置ab策略信息，用于tti数据ab分组
    this.setCustomTags(this.abInfo);
  }
  /**
   * 获取项目各页面、各模块的动态加载时长，在pgyDirective中通过指令的方式输出。
   * @param  Element el      目标元素
   * @param  Object binding  目标元素绑定的数据
   */
  collect(el, binding) {
    try {
      const isInFirstScreen = this.isInFirstScreen(el);
      if (!isInFirstScreen || this.stopCollectFlag) return;
      const url = `${window.location.hostname}${window.location.pathname}`;
      const bindingValue = (binding && binding.value) || {};
      const moduleName = bindingValue.moduleName ? `module_${bindingValue.moduleName}` : bindingValue.moduleName;
      const time = Date.now() - window.performance.timing.navigationStart;
      if (!moduleName) {
        console.error(`v-metric-collect的必传参数moduleName缺失，影响对应数据收集`);
      }
      if (!this.times[url]) {
        this.times[url] = {};
      }
      this.times[url][moduleName] = this.times[url][moduleName] || time;
      if (bindingValue.extraData) {
        this.extra[moduleName] = bindingValue.extraData;
      }
    } catch (error) {
      this.handleException(error);
    }
  }

  /**
   * 在页面进入或者离开时上报数据。上报数据格式：
   * {"kvs":{"moduleA":`${t0}`, "moduleB":`${t1}`, "diff_time":'${渲染时间}'},"tags":{"url":`${url}`,"ab":${ab}},"ts":1712645789,"extraData":`${extraData}`}
   * 其中：kvs中 moduleA|moduleB 上报动态模块加载时长，diff_time上报页面动态模块最大渲染时差
   * @param  Object data  需要上报的数据
   */
  report(data = {}) {
    try {
      // 获取模块数据、ab信息、额外参数、收集数据日期
      const { times, abInfo, extra } = this;

      if (window.Owl.MetricManager) {
        const metricManager = window.Owl.MetricManager();
        if (!times) return;
        Object.keys(times).forEach(url => {
          const pageTimes = times[url];
          if (!pageTimes) return;
          const pageTimesArr = objectValues(pageTimes);
          const pageRenderDt = this.findMinMaxDiff(pageTimesArr);
          objectEntries(pageTimes).forEach(([key, value]) => {
            metricManager.setMetric(key, value);
          });
          metricManager.setTags({ url, ...abInfo });
          metricManager.setMetric('diff_time', pageRenderDt);
          metricManager.setExtraData(extra);
          metricManager.report();
        });
        this.resetData();
        // 离开页面时清除事件
        this.componentWillUnmount();
      }
    } catch (error) {
      this.handleException(error)
    }
  }
  /**
   * 数据上报之后重置数据
   */
  resetData() {
    this.times = {};
    this.extra = {};
    this.stopElems = [];
    this.stopCollectFlag = false;
  }
  /**
   * 工具函数异常统一上报
  */
  handleException(error) {
    window.Owl.addError({
      name: 'metric-collect',
      msg: error
    }, {
      level: 'warn'
    });
  }
  /**
   * 自动监听页面离开事件，将当前访问数据（同步渲染情况，页面动态模块加载时间）进行上报
   */
  autoReport() {
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    window.addEventListener('beforeunload', this.reportHandler);
    window.addEventListener('KNB:disappear', this.reportHandler);
    this.subscribEvent('background', this.reportHandler);
  }
  /**
   * 离开页面后，清空事件监听
   */
  componentWillUnmount() {
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    window.removeEventListener('beforeunload', this.reportHandler);
    window.removeEventListener('KNB:disappear', this.reportHandler);
    window.removeEventListener('touchstart', this.stopCollectHandler)
  }
  /**
   * 添加touchstart定时器
   */
  stopListener () {
    window.addEventListener('touchstart', this.stopCollectHandler)
  }
  /**
   * 通过knb监听页面离开事件
   * @param  String type      knb api
   * @param  Function handler 事件监听
   */
  subscribEvent (type, handler) {
    if (typeof window === 'undefined') return;
    window.KNB && window.KNB.ready(() => {
      window.KNB && window.KNB.subscribe({
        action: type,
        handle: function () {
          handler()
        },
        success: function (data) {
          window.KNB && window.KNB.unsubscribe({
            action: type, // 取消该所有事件的订阅；慎用，会影响所有对该事件的订阅
            subId: data.subId // 取消 subId 对应事件的订阅
          });
        }
      });
    })
  }
  /**
   * visibilitychange回调函数
   */
  handleVisibilityChange () {
    if (document.visibilityState === 'hidden') {
      this.report();
    }
  }
  /**
    * 监听元素高度变化，用来收集模块初始高度为0的渲染数据
    * @param  Element el      目标元素
    * @param  Object binding  目标元素绑定的数据
    */
  mutationObseverHandler (el, binding) {
    try {
      const MutationObserver = window.MutationObserver || window.webkitMutationObserver || window.MozMutationObserver;
      // MutationObserver不存在或者__observer__实例已存在时直接返回
      if (!MutationObserver || el.__observer__) return;
      const onHeightChange = function () {
        let { height } = el.getBoundingClientRect();
        // 当前元素不包含对照组骨架屏、高度不为空、当前元素实例存在时进行数据采集
        if (!collectInstance.hasSkeletonEle(el) && height && el.__observer__) {
          el.setAttribute('metric-collect', true);
          collectInstance.collect(el, binding);
          // 清除MutationObserver
          el.__observer__.disconnect();
          el.__observer__ = null;
        }
      };
      // 初始化MutationObserver，并添加高度监听回调
      el.__observer__ = new MutationObserver((mutations) => {
          onHeightChange();
      });
      // 指定目标元素，监听高度变动
      el.__observer__.observe(el, {
        childList: true, // 监听子节点的添加和移除
        subtree: true,
        attributeFilter: ['style', 'class'], // 观察特定属性
        attributes: true
      });
    } catch (error) {
      this.handleException(error);
    }
  };
  /**
   * 目标元素绑定指令的回调函数，用来收集模块渲染数据
   * @param  Element el      目标元素
   * @param  Object binding  目标元素绑定的数据
   */
  obseverHandler (el, binding) {
    const ao = new AppearObserver({
      mode: 'normal'
    })
    const els = Array.isArray(el) ? el : [el];
    ao.observe(els, {
      handle(ev) {
        const { height } = ev.target.getBoundingClientRect();
        const flag = ev.target.getAttribute('metric-collect');
        // 元素未出现在首屏直接返回
        if (!ev.isAppear) return;
        // height高度为0、存在骨架屏继续监听当前元素高度变化
        if (!height || collectInstance.hasSkeletonEle(ev.target)) {
          collectInstance.mutationObseverHandler(ev.target, binding);
        // 元素曝光打点
        } else if (ev.isAppear === true && !flag) {
          ev.target.setAttribute('metric-collect', true)
          collectInstance.collect(ev.target, binding)
          ao.removeElements([ev.target])
        }
      }
    })
  }
  /**
   * 判断当前元素是否在首屏
   * @param  Element element  目标元素
   * @return Boolean          当前元素是否在首屏
   */
  isInFirstScreen(element) {
    const rect = element && element.getBoundingClientRect && element.getBoundingClientRect();
    if (!rect) return false;
    return rect.bottom <= window.innerHeight;
  }
  /**
   * 判断当前元素是否包含对照组骨架屏
   * @param  Element element  目标元素
   * @return Boolean          当前元素是否包含对照组骨架屏
   */
  hasSkeletonEle(element) {
    const skeletonEle = element.querySelector('.pgy-skeleton, .metric-collect-skeleton');
    return skeletonEle && skeletonEle.style.display !== 'none';
  }
  /**
   * 判断当前元素是否会影响页面模块重新加载
   * @param  Event event 事件
   */
  handleStopCollect (event) {
    if (this.stopElems && this.stopElems.includes(event.target)) {
      this.stopCollectFlag = true;
    }
  }
  /**
   * 收集停止数据收集的元素
   * @param  Element el 停止数据收集的元素元素
   */
  stopCollect (el) {
    this.stopElems = this.stopElems && this.stopElems.concat([el])
  }
  /**
   * 获取模块渲染时长最大值与最小值的时差
   * @param  Array array 各模块渲染时长
   * @return Number      渲染时差
   */
  findMinMaxDiff(array) {
    const min = Math.min(...array);
    const max = Math.max(...array);
    return max - min;
  }
  /**
   * 获取AB策略以及策略开关，用来上报至模块tags方便条件过滤
   * @param  Object abInfo 优先获取接口返回的ab信息
   * @return Object `${layerId}` - ab实验策略；`${layerId}_s` - ab实验策略对应的开关
   */
  getAbResult (cookieValue) {
    try {
        // 接口返回ab信息，有则直接赋值
        const result = cookieValue || getABFromCookie({ abType: this.abType, cookieConfig: this.cookieConfig });
        // 设置window._PGY_GLOBAL_CONFIG._PGY_WINK_COOKIE全局标识供PGY-SDK使用
        window._PGY_GLOBAL_CONFIG = { ...window._PGY_GLOBAL_CONFIG, _PGY_WINK_COOKIE: result }
        // abInfo赋值
        this.abInfo = result.report || {};
    } catch (error) {
        this.handleException(error);
    }
  }

  /**
   * 用于获取业务自定义维度信息（owl版本1.10.2+）
   * @param Object tags 自定义信息
   */
  setCustomTags(tags) {
    try {
      if (window.owl && typeof window.owl === 'function') {
        window.owl('config', {
          setCustomTags: () => {
            return tags;
          },
        });
      }
    } catch (error) {
      this.handleException(error);
    }
  }
}
